/**
 * src/pages/reports/_reportErrors.js — what a reader is told when something
 * goes wrong. Card P230 item 2.
 *
 * ⛔⛔ NO STACK TRACE, NO STATUS CODE, NO LIBRARY NAME ON SCREEN. The CEO
 * report has already put two of those in front of Carlos this week: a raw
 * JSON document (P209) and, had the WinAnsi throw reached production rather
 * than my first local build, the words "pdf-lib WinAnsi cannot encode" under
 * a Download button. A reader who sees a library name learns nothing they can
 * act on and loses confidence in the figures around it.
 *
 * ⚠⚠ THE TECHNICAL DETAIL IS NOT DISCARDED — IT MOVES. Every mapping here
 * returns the message for the screen; the caller logs the original error to
 * the console, where it is for whoever is debugging rather than for whoever
 * is reading the report.
 *
 * ⚠ A CLASSIFIER, NOT A LOOKUP. The same cause reaches us in several shapes
 * across Electron, the web bridge and the browser's own APIs — a timeout is
 * an Error with "timeout" in it from Bedrock, an AbortError from fetch, and a
 * plain string from the IPC layer. Matching on SHAPE rather than on an exact
 * message is why this does not quietly fall through to the generic text the
 * first time a layer rewords something.
 */

/** Every message the reader can see, keyed by cause. */
export const MESSAGES = Object.freeze({
  timeout: {
    text: 'The report took too long to put together. Please try again in a minute.',
    retry: true,
  },
  offline: {
    text: "We couldn't reach the server. Check your internet connection and try again.",
    retry: true,
  },
  forbidden: {
    text: "Your account doesn't have permission to download reports. "
        + 'Ask an administrator to turn on report exports.',
    /* ⛔ NO RETRY. Pressing a button again cannot grant a permission, and
       offering it teaches the reader to keep pressing. */
    retry: false,
  },
  signedOut: {
    text: "You've been signed out. Please sign in again, then regenerate the report.",
    retry: false,
  },
  pdfBuild: {
    text: "We couldn't create the PDF this time. You can still use Print, then "
        + "'Save as PDF', or try Download again.",
    retry: true,
  },
  downloadBlocked: {
    text: 'Your browser blocked the download. Allow downloads for this site and try again.',
    retry: true,
  },
  unknown: {
    text: 'Something went wrong creating the report. Please try again. '
        + 'If it keeps happening, let Daniel know.',
    retry: true,
  },
});

const has = (hay, needle) => hay.indexOf(needle) !== -1;

/**
 * Classify an error into one of the causes above.
 *
 * @param {*}      err      an Error, a string, or a server result object
 * @param {string} context  'generate' | 'pdf' | 'csv' | 'print'
 */
export function classifyError(err, context = 'generate') {
  const code = String((err && (err.code || err.name)) || '').toLowerCase();
  const raw = String(
    (err && (err.message || err.error_description)) || err || '',
  ).toLowerCase();
  const status = Number(err && (err.status || err.statusCode)) || 0;

  /* ── Permission and session come first: they are unambiguous and the
       advice for them is different from everything else. ──────────────── */
  if (status === 401 || has(code, 'unauthenticated') || has(raw, 'signed out')
      || has(raw, 'session expired') || has(raw, 'not authenticated')) {
    return 'signedOut';
  }
  if (status === 403 || has(code, 'access_denied') || has(code, 'forbidden')
      || has(raw, 'access denied') || has(raw, 'permission')
      || has(raw, 'not permitted') || has(raw, 'forbidden')) {
    return 'forbidden';
  }

  /* ── Reachability. ⚠ An AbortError from fetch is a TIMEOUT, not a network
       failure, so it is matched before the offline shapes. ─────────────── */
  if (has(code, 'aborterror') || has(raw, 'timeout') || has(raw, 'timed out')
      || has(raw, 'took too long') || has(raw, 'deadline')) {
    return 'timeout';
  }
  if (has(code, 'networkerror') || has(raw, 'failed to fetch')
      || has(raw, 'network') || has(raw, 'offline')
      || has(raw, 'err_internet_disconnected') || has(raw, 'load failed')) {
    return 'offline';
  }

  /* ── Browser download policy. ─────────────────────────────────────────── */
  if (has(raw, 'download') && (has(raw, 'block') || has(raw, 'denied'))) {
    return 'downloadBlocked';
  }

  /*
   * ── The PDF builder. ⛔ CONTEXT DECIDES, NOT THE TEXT. A font or encoding
   * failure comes back as "WinAnsi cannot encode" or "fontkit" or an embed
   * error, and trying to enumerate those strings is how the generic message
   * ends up in front of a reader instead of the useful one. Anything that
   * fails while building a PDF IS a PDF build failure.
   */
  if (context === 'pdf') return 'pdfBuild';

  return 'unknown';
}

/**
 * The message for an error. Returns { cause, text, retry }.
 *
 * ⚠ The returned text NEVER contains the original message. That is the whole
 * contract, and a red arm plants Error('pdf-lib WinAnsi cannot encode') to
 * prove the words do not reach the screen.
 */
export function messageForError(err, context = 'generate') {
  const cause = classifyError(err, context);
  const m = MESSAGES[cause] || MESSAGES.unknown;
  return { cause, text: m.text, retry: m.retry };
}
